import { get, isNil } from "lodash";

const parseFormattedEuro = (value) => {
  if (!value) return null;
  const normalized = String(value)
    .replace(/[^\d,.-]/g, "")
    .replace(/\.(?=\d{3}(?:\D|$))/g, "")
    .replace(",", ".");
  const amount = Number(normalized);
  return Number.isFinite(amount) ? amount : null;
};

const yearlyFromMonthlyPrice = (formatted) => {
  const monthly = parseFormattedEuro(formatted);
  if (monthly == null) return "";
  return (
    (monthly * 12).toLocaleString("fr-FR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + " €"
  );
};

const formatPersonCost = (cost, currency) =>
  isNil(cost)
    ? ""
    : `${Number(cost).toFixed(2)} ${currency === "EUR" ? "€" : currency || ""}`.trim();

const personPrices = (planCost) => {
  const costDetails = get(planCost, "costDetails");
  if (!costDetails) return {};
  const currency = get(planCost, "currency");
  const spousePrice = formatPersonCost(
    get(costDetails, "spouseCost"),
    currency,
  );
  const childrenPrice = formatPersonCost(
    get(costDetails, "childrenCost"),
    currency,
  );
  return {
    adherentPrice: formatPersonCost(get(costDetails, "adherentCost"), currency),
    spousePrice,
    childrenPrice,
    hasSpouse: spousePrice ? "true" : "false",
    hasChildren: childrenPrice ? "true" : "false",
  };
};

export const RECOMMENDED_POSITION = {
  ATTRIBUTE: "data-recommended-position",
  CENTER: "center",
};

export const centerRecommended = (items) => {
  const index = items.findIndex((item) => item.recommended === "true");
  if (index < 0) return items;
  const rest = items.filter((_, position) => position !== index);
  const middle = Math.floor(items.length / 2);
  return [...rest.slice(0, middle), items[index], ...rest.slice(middle)];
};

export const ITEM_FLAGS = {
  plans: ["recommended", "hasSpouse", "hasChildren"],
};

export const catalogFromStatusData = (data) => ({
  attachments: (data && data.attachments) || [],
});

const toPlanItems = (ordered, planPrices, project, planCosts) => {
  const subscriptions =
    (project && project.fields && project.fields.subscriptions) || {};
  return ordered.map((plan) => {
    const price = (planPrices && planPrices[plan.id]) || "";
    const yearly = yearlyFromMonthlyPrice(price);
    const subscriptionId = subscriptions[plan.id] || "";
    const selectedPlan =
      project && project.fields && project.fields.selectedPlan;
    const quotedPlans =
      (project && project.fields && project.fields.quotedPlans) || [];
    const quotedByWeb =
      quotedPlans.indexOf(plan.id) !== -1 ||
      (Boolean(project && project.locked) && selectedPlan === plan.id);
    return {
      ...plan,
      ...(price ? { price } : {}),
      ...(yearly ? { yearly } : {}),
      ...personPrices(planCosts && planCosts[plan.id]),
      subscriptionId,
      quoted: subscriptionId || quotedByWeb ? "true" : "false",
    };
  });
};

const isBetterRecommendation = (candidate, current) => {
  if (candidate.score !== current.score) return candidate.score < current.score;
  if (isNil(candidate.amount)) return false;
  return isNil(current.amount) || candidate.amount < current.amount;
};

const markRecommendedPlans = (items, planScores) => {
  const ranked = items
    .filter((item) => !isNil(get(planScores, item.id)))
    .map((item) => ({
      id: item.id,
      score: Number(planScores[item.id]),
      amount: parseFormattedEuro(item.price),
    }));
  if (!ranked.length) return items;
  const best = ranked.reduce((current, candidate) =>
    isBetterRecommendation(candidate, current) ? candidate : current,
  );
  return items.map((item) => ({
    ...item,
    recommended: item.id === best.id ? "true" : "false",
  }));
};

const reinforcementItems = ({ upsellPlans, planPrices, project }) =>
  toPlanItems(upsellPlans || [], planPrices, project);

export const isReinforcementSource = (source) =>
  source === "upsells" || source === "renforts";

export const PLAN_SELECTION_MANY = "many";

const withPlanUpsells = (items, ctx) => {
  const { upsellsByPlan, upsellPlans, planPrices, planCosts, project } = ctx;
  if (!upsellsByPlan) return items;
  const loaded = upsellPlans || [];
  return items.map((item) => {
    const links = (upsellsByPlan[item.id] || []).filter(({ id }) =>
      loaded.some((plan) => plan.id === id),
    );
    const upsells = toPlanItems(
      links.map(({ id }) => loaded.find((plan) => plan.id === id)),
      planPrices,
      project,
      planCosts,
    ).map((upsell, index) => ({
      ...upsell,
      ...((planCosts && planCosts[upsell.id]) || {}),
      weight: links[index].weight,
    }));
    return {
      ...item,
      upsells,
      hasUpsells: upsells.length ? "true" : "false",
    };
  });
};

const sources = {
  plans: (ctx) => {
    const {
      insurancePlans,
      planPrices,
      planCosts,
      planScores,
      project,
      planSelection,
    } = ctx;
    const selectedIds =
      planSelection === PLAN_SELECTION_MANY
        ? []
        : (project && project.fields && project.fields.selectedToCompare) || [];
    const all = insurancePlans || [];
    const ordered = selectedIds.length
      ? selectedIds
          .map((id) => all.find((plan) => plan.id === id))
          .filter(Boolean)
      : all;
    return withPlanUpsells(
      markRecommendedPlans(
        toPlanItems(ordered, planPrices, project, planCosts),
        planScores,
      ),
      ctx,
    );
  },
  upsells: reinforcementItems,
  renforts: reinforcementItems,
};

export const getCollectionItems = (collectionName, ctx) => {
  const source = sources[collectionName];
  return source ? source(ctx) : [];
};
